//import * as _ from 'underscore';
import axiosStatic, { AxiosInstance } from "axios";
import { IAuthenticationConfiguration } from "./IAuthenticationConfiguration";
import { IAuthenticationStatus } from "./IAuthenticationStatus";
import { IUserInfo } from "./IUserInfo";
import Observable from "./Observable";

interface ITokens {
    refreshToken?: string;
    refreshTokenExpires?: number;
    accessToken?: string;
    accessTokenExpires?: number;
}

interface ILoginResponse {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    refresh_token_exp: number;
    token_type: string;
    user_info: IUserInfo;
}

interface IImpersonateResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
    user_info: IUserInfo;
}

//polyfill will take care of making it actually work
//don't know why typescript won't recognise Promise though
//declare var Promise: { resolve: () => Promise<void>, reject: () => Promise<never> };

function statusEquals(
    status1: IAuthenticationStatus,
    status2: IAuthenticationStatus
) {
    return (
        status1 === status2 ||
        JSON.stringify(status1) === JSON.stringify(status2)
    );
}

declare global {
    var PasswordCredential: any;
}

export interface IAuthenticationClient {
    GetPendingOperations: () => number;
    Dispose: () => void;
    Status: Observable<IAuthenticationStatus>;
    GetStatus: () => IAuthenticationStatus;
    GetAccessToken: () => string | null;
    Login: (
        email: string,
        password: string,
        rememberMe: boolean
    ) => Promise<void>;
    Logout: () => Promise<void>;
    CheckRegistered: (email: string) => Promise<boolean>;
    ChangeEmail: (newEmail: string, verifyPassword: string) => Promise<void>;
    ChangePassword: (oldPassword: string, newPassword: string) => Promise<void>;
    UpgradeGuest: (
        name: string,
        email: string,
        password: string,
        rememberMe: boolean
    ) => Promise<void>;
    UpgradeGuestForOrder: (
        clubOrderGuid: string,
        password: string,
        rememberMe: boolean
    ) => Promise<void>;
    ResetPasswordRequest: (email: string) => Promise<boolean>;
    ResetPassword: (
        resetToken: string,
        email: string,
        newPassword: string,
        rememberMe: boolean
    ) => Promise<void>;
    Impersonate: (userId: string) => Promise<void>;
    ImpersonateQuit: () => Promise<void>;
    EnsureGuest: (rememberMe: boolean) => Promise<void>;
    UpdateToken: () => Promise<void>;
    ChangeGuestEmail: (newEmail: string) => Promise<void>;
    TryAutoLogin: () => Promise<void>;
    SaveCookieRedirect: (redirectTo: string) => Promise<void>;
}

export default class Authentication implements IAuthenticationClient {
    private config: IAuthenticationConfiguration;
    private status: IAuthenticationStatus;
    private lastStatus: IAuthenticationStatus;
    private tokens: ITokens = {};
    private loggedOutStatus: IAuthenticationStatus = {
        loggedIn: false,
        isGuest: false,
        isAdmin: false,
        isRegistered: false,
        impersonating: false,
        userInfo: null,
        rememberMe: false,
    };
    private axios: AxiosInstance;
    private axiosAdmin: AxiosInstance;
    private pendingOperations: number = 0;

    public constructor(configuration: IAuthenticationConfiguration) {
        this.config = configuration;
        this.status = this.loggedOutStatus;
        this.lastStatus = this.loggedOutStatus;
        this.axios = axiosStatic.create({ baseURL: this.config.baseUrl });
        this.axios.defaults.withCredentials = true;
        const oldpost = this.axios.post;
        this.axios.post = (a, b, c) => {
            this.pendingOperations += 1;
            return oldpost(a, b, c).then(
                (data) => {
                    this.pendingOperations -= 1;
                    return Promise.resolve(data) as any;
                },
                (data) => {
                    this.pendingOperations -= 1;
                    return Promise.reject(data);
                }
            );
        };
        this.axiosAdmin = axiosStatic.create({ baseURL: this.config.adminUrl });
        this.axiosAdmin.defaults.withCredentials = true;
        window.addEventListener("storage", this.onStorageEvent);
    }

    public GetPendingOperations = () => {
        return this.pendingOperations;
    };

    public Dispose = () => {
        window.removeEventListener("storage", this.onStorageEvent);
    };

    public Status = new Observable<IAuthenticationStatus>();

    public GetStatus = (): IAuthenticationStatus => {
        return { ...this.status };
    };

    public GetAccessToken = () => {
        return (this.status.loggedIn ? this.tokens.accessToken : null) || null;
    };

    private pushStatus = () => {
        if (statusEquals(this.status, this.lastStatus)) return;
        this.lastStatus = this.status;
        const status = { ...this.status };
        this.Status.notify(status);
    };

    public Login = (email: string, password: string, rememberMe: boolean) => {
        //upon failure, returns a failure, and does not log out
        return this.loginWithUsername(email, password, rememberMe);
    };

    public Logout = (): Promise<void> => {
        //always returns success
        if (!this.status.loggedIn) return Promise.resolve();
        this.status = {
            impersonating: false,
            isGuest: false,
            isAdmin: false,
            isRegistered: false,
            loggedIn: false,
            rememberMe: false,
            userInfo: null,
        };
        this.updateSession();
        this.pushStatus();
        return Promise.resolve();
    };

    public CheckRegistered = (email: string) => {
        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        postData.append("email", email);
        return this.axios
            .post<{ email: string; hasValidAccount: boolean }>(
                "checkregistered",
                postData
            )
            .then(
                (res) => res.data.hasValidAccount,
                (res) => {
                    console.error(
                        "Unable to check if email account was registered",
                        email,
                        res
                    );
                    return false;
                }
            );
    };

    public ChangeEmail = (newEmail: string, verifyPassword: string) => {
        //returns an error if not logged on or invalid password
        if (
            !this.status.loggedIn ||
            this.status.isGuest ||
            !this.tokens.refreshToken
        )
            return Promise.reject();
        const postData = new FormData();
        postData.append("password", verifyPassword);
        postData.append("refreshToken", this.tokens.refreshToken);
        postData.append("newEmail", newEmail);
        postData.append("client_id", this.config.clientId);
        return this.axios
            .post<ILoginResponse>("changeemail", postData)
            .then((res) => {
                this.processLoginResponse(res.data, this.status.rememberMe);
            });
    };

    public ChangePassword = (oldPassword: string, newPassword: string) => {
        //returns an error if not logged on or invalid password
        if (
            !this.status.loggedIn ||
            this.status.isGuest ||
            !this.tokens.refreshToken
        )
            return Promise.reject();
        const postData = new FormData();
        postData.append("newPassword", newPassword);
        postData.append("oldPassword", oldPassword);
        postData.append("refreshToken", this.tokens.refreshToken);
        postData.append("client_id", this.config.clientId);
        return this.axios
            .post<ILoginResponse>("changepassword", postData)
            .then((res) => {
                this.processLoginResponse(res.data, this.status.rememberMe);
            });
    };

    public UpgradeGuest = (
        name: string,
        email: string,
        password: string,
        rememberMe: boolean
    ) => {
        //returns an error if not logged on or not guest or invalid password
        if (
            !this.status.loggedIn ||
            !this.status.isGuest ||
            !this.tokens.refreshToken
        )
            return Promise.reject();
        const postData = new FormData();
        postData.append("refreshToken", this.tokens.refreshToken);
        postData.append("name", name);
        postData.append("email", email);
        postData.append("password", password);
        postData.append("client_id", this.config.clientId);
        return this.axios
            .post<ILoginResponse>("upgradeguest", postData)
            .then((res) => {
                this.processLoginResponse(res.data, rememberMe);
            });
    };

    public UpgradeGuestForOrder = (
        clubOrderGuid: string,
        password: string,
        rememberMe: boolean
    ) => {
        const postData = new FormData();
        postData.append("clubOrderGuid", clubOrderGuid);
        postData.append("password", password);
        postData.append("client_id", this.config.clientId);
        return this.axios
            .post<ILoginResponse>("upgradeguestfororder", postData)
            .then((res) => {
                this.processLoginResponse(res.data, rememberMe);
            });
    };

    public ResetPasswordRequest = (email: string) => {
        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        postData.append("email", email);
        return this.axios
            .post<void>("resetpasswordrequest", postData, {
                validateStatus: (status) => status === 200 || status === 400,
            })
            .then((res) => {
                if (res.status === 400) return false;
                return true;
            });
    };

    public ResetPassword = (
        resetToken: string,
        email: string,
        newPassword: string,
        rememberMe: boolean
    ) => {
        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        postData.append("resetToken", resetToken);
        postData.append("newPassword", newPassword);
        postData.append("email", email);
        return this.axios
            .post<ILoginResponse>("resetpassword", postData)
            .then((res) => {
                this.processLoginResponse(res.data, rememberMe);
            });
    };

    public Impersonate = (userId: string) => {
        //returns a failure if not logged in or if failure during impersonation request
        if (!this.status.loggedIn) return Promise.reject();
        if (!this.tokens.refreshToken || !this.tokens.refreshTokenExpires)
            return this.Logout().then(() => {
                return Promise.reject();
            });
        const postData = new FormData();
        postData.append("refreshToken", this.tokens.refreshToken);
        postData.append("userId", userId);
        postData.append("client_id", this.config.clientId);
        return this.axios
            .post<IImpersonateResponse>("impersonate", postData)
            .then((res) => {
                const now = new Date().getTime();
                this.tokens.accessToken = res.data.access_token;
                this.tokens.accessTokenExpires =
                    res.data.expires_in * 1000 + now;
                this.status = {
                    impersonating: true,
                    isGuest: false,
                    isAdmin:
                        res.data.user_info.roles.filter(
                            (x) => x === "Administrators"
                        ).length > 0,
                    loggedIn: true,
                    isRegistered: true,
                    rememberMe: this.status.rememberMe,
                    userInfo: {
                        email: res.data.user_info.email,
                        id: res.data.user_info.id + "",
                        name: res.data.user_info.name,
                        roles: res.data.user_info.roles,
                    },
                };
                this.updateSession();
                this.pushStatus();
            });
    };

    public ImpersonateQuit = (): Promise<void> => {
        //attempt to log in as original user, or else just log out
        //always return success
        return this.refreshToken(true);
    };

    public EnsureGuest = (rememberMe: boolean): Promise<void> => {
        //attempts to create a guest if not logged in
        //fails if createguest fails
        this.checkSession();
        if (this.status.loggedIn) return Promise.resolve();

        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        return this.axios
            .post<ILoginResponse>("createguest", postData)
            .then((res) => {
                this.processLoginResponse(res.data, rememberMe);
            });
    };

    public UpdateToken = (): Promise<void> => {
        //inspects session state to see if it's changed
        //refreshes any tokens necessary
        //catches any errors
        //updates status if it has changed
        //returns promise when all that's done
        this.checkSession();
        if (
            this.status.loggedIn &&
            this.tokens.refreshTokenExpires &&
            this.tokens.accessTokenExpires
        ) {
            const now = new Date().getTime();
            if (this.tokens.refreshTokenExpires < now) {
                return this.Logout();
            } else if (this.tokens.accessTokenExpires < now) {
                return this.refreshToken(false);
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    };

    public ChangeGuestEmail = (newEmail: string): Promise<void> => {
        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        postData.append("newEmail", newEmail);
        if (this.tokens.refreshToken)
            postData.append("refreshToken", this.tokens.refreshToken);
        return this.axios
            .post<ILoginResponse>("changeguestemail", postData)
            .then((res) => {
                if (res.status === 400) alert("400");
                if (res.status === 401) alert("401");
                else {
                    this.processLoginResponse(res.data, false);
                }
            });
    };

    public TryAutoLogin = (): Promise<void> => {
        //this does nothing anymore, but it should check if any other tabs are logged in
        this.checkSession();
        if (this.status.loggedIn) return Promise.resolve();

        //check session cookie to see if the state is in there
        const authCookies = document.cookie
            .split(";")
            .map((x) => x.trim())
            .filter((x) => x.substr(0, 15) === "authentication=")
            .map((x) => x.substr(15));
        if (authCookies.length > 0) {
            //console.log('Found auth cookie');
            this.decodeAuthSessionString(decodeURIComponent(authCookies[0]));
            //console.log(`Decoded auth cookie ${this.status.loggedIn}`)
            if (this.status.loggedIn) return this.UpdateToken();
        }

        //pull token from localStorage and attempt to log in
        //always return success
        var authDataString = localStorage.getItem("Authentication");
        if (authDataString === null) return Promise.resolve();
        //console.log('Found auth localStorage string');
        let tokenData: {
            refreshToken: string;
            refreshTokenExpires: number;
            version: "1";
        };
        try {
            tokenData = JSON.parse(authDataString);
        } catch {
            return Promise.resolve();
        }
        const now = new Date().getTime();
        if (
            tokenData &&
            tokenData.version === "1" &&
            tokenData.refreshToken &&
            tokenData.refreshTokenExpires &&
            tokenData.refreshTokenExpires > now
        ) {
            //console.log('Parsed auth localStorage string');
            return this.loginWithToken(tokenData.refreshToken, true).then(
                null,
                () => {
                    //console.log("Removing localStorage string");
                    localStorage.removeItem("Authentication");
                    return;
                }
            );
        }
        return Promise.resolve();
    };

    //This function connects to the auth controller of the same name: SaveCookie(string client_id, string refreshToken)
    public SaveCookieRedirect = (redirectTo: string): Promise<void> => {
        if (!this.status.loggedIn || !this.tokens.refreshToken)
            return Promise.reject();

        /*
        //send post request to auth controller passing in client id and refesh token (assuming there is a token)
        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        postData.append("refreshToken", this.tokens.refreshToken);

        //return promise of save cookie completing
        return this.axiosAdmin.post<void>('savecookie', postData)
            .then(
                res => {
                    //console.log(`savecookie response code ${res.status}`)
                    return;
                }
            );
        */

        return this.UpdateToken().then(() => {
            const token = this.GetAccessToken();
            if (!token) return Promise.reject();
            window.location.href =
                this.config.adminUrl +
                "/savecookie?client_id=" +
                encodeURIComponent(this.config.clientId) +
                "&accessToken=" +
                encodeURIComponent(token) +
                "&redirectTo=" +
                encodeURIComponent(redirectTo);
            return;
        });
    };

    // ============== private members ==================

    private refreshTokenPending: Promise<void> | null = null;
    private refreshToken = (dontImpersonate: boolean): Promise<void> => {
        //force a refresh of the tokens
        //always return success
        if (this.refreshTokenPending != null) return this.refreshTokenPending;
        if (!this.status.loggedIn) return Promise.resolve();
        if (
            this.tokens.refreshToken === undefined ||
            this.tokens.refreshTokenExpires === undefined
        )
            return this.Logout();
        if (this.status.impersonating && !dontImpersonate) {
            this.refreshTokenPending = this.Impersonate(
                this.status.userInfo.id
            ).then(null, () => {
                return this.Logout();
            });
        } else {
            this.refreshTokenPending = this.loginWithToken(
                this.tokens.refreshToken,
                this.status.rememberMe
            ).then(null, () => {
                return this.Logout();
            });
        }
        this.refreshTokenPending = this.refreshTokenPending.then(null, () => {
            return this.Logout();
        });
        this.refreshTokenPending = this.refreshTokenPending.then(
            () => {
                this.refreshTokenPending = null;
            },
            () => {
                this.refreshTokenPending = null;
            }
        );
        return this.refreshTokenPending;
    };

    private loginWithToken = (refreshToken: string, rememberMe: boolean) => {
        //does not handle error from axios or logout
        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        postData.append("grant_type", "refresh_token");
        postData.append("refresh_token", refreshToken);
        return this.axios
            .post<ILoginResponse>("token", postData)
            .then((res) => {
                this.processLoginResponse(res.data, rememberMe);
            });
    };

    private loginWithUsername = (
        username: string,
        password: string,
        rememberMe: boolean
    ) => {
        //does not handle error from axios or logout
        const postData = new FormData();
        postData.append("client_id", this.config.clientId);
        postData.append("grant_type", "password");
        postData.append("username", username);
        postData.append("password", password);
        if (
            this.status.loggedIn &&
            this.status.isGuest &&
            this.tokens.refreshToken
        )
            postData.append("guest_refresh_token", this.tokens.refreshToken);
        return this.axios
            .post<ILoginResponse>("token", postData)
            .then((res) => {
                this.processLoginResponse(res.data, rememberMe);
                if (
                    (window as any).PasswordCredential &&
                    navigator.credentials &&
                    navigator.credentials.store
                ) {
                    const credential = new PasswordCredential({
                        id: username,
                        password: password,
                    });
                    navigator.credentials.store(credential);
                }
            });
    };

    private processLoginResponse = (
        data: ILoginResponse,
        rememberMe: boolean
    ) => {
        const now = new Date().getTime();
        this.tokens = {
            refreshToken: data.refresh_token,
            refreshTokenExpires: data.refresh_token_exp * 1000 + now,
            accessToken: data.access_token,
            accessTokenExpires: data.expires_in * 1000 + now,
        };

        const isGuest: boolean = data.user_info.roles.indexOf("Guests") >= 0;
        //console.log("GUEST: " + isGuest)

        if (isGuest) {
            this.status = {
                impersonating: false,
                isGuest: true,
                isAdmin: false,
                loggedIn: true,
                isRegistered: false,
                rememberMe: rememberMe,
                userInfo: {
                    email: data.user_info.email,
                    id: data.user_info.id + "",
                    roles: data.user_info.roles,
                    name: data.user_info.name,
                },
            };
        } else {
            this.status = {
                impersonating: false,
                isGuest: false,
                isAdmin:
                    data.user_info.roles.filter((x) => x === "Administrators")
                        .length > 0,
                loggedIn: true,
                isRegistered: true,
                rememberMe: rememberMe,
                userInfo: {
                    email: data.user_info.email,
                    id: data.user_info.id + "",
                    roles: data.user_info.roles,
                    name: data.user_info.name,
                },
            };
        }
        this.updateSession();
        this.pushStatus();
    };

    private onStorageEvent = (ev: StorageEvent) => {
        //console.log('Received auth keys from another tab');
        if (ev.key === "AuthenticationSession" && ev.newValue)
            this.decodeAuthSessionString(ev.newValue);
    };

    private checkSession = (): void => {
        //this function is to check other tabs to see if they are logged in (if tokens/status is different)
        //since there's no practical way to do this, this function is a noop
    };

    private decodeAuthSessionString = (authDataString: string): void => {
        if (authDataString === null) return;
        let authData: {
            status: IAuthenticationStatus;
            tokens: ITokens;
            version: "1";
        };
        try {
            authData = JSON.parse(authDataString);
        } catch {
            return;
        }
        const now = new Date().getTime();
        if (
            authData &&
            authData.version === "1" &&
            authData.status &&
            authData.tokens &&
            authData.tokens.refreshToken &&
            authData.tokens.refreshTokenExpires &&
            authData.tokens.refreshTokenExpires > now
        ) {
            this.tokens = authData.tokens;
            if (!statusEquals(authData.status, this.status)) {
                this.status = authData.status;
                this.pushStatus();
            }
        }
    };

    private updateSession = (): void => {
        //create a string containing tokens and status to pass between tabs
        const authString = JSON.stringify({
            status: this.status,
            tokens: this.tokens,
            version: "1",
        });
        //set and remove from localstorage to trigger a storage event (pass data between active tabs)
        localStorage.setItem("AuthenticationSession", authString);
        localStorage.removeItem("AuthenticationSession");
        //note that sessionStorage is local only to the individual tab, and cookies only update upon refresh
        //cannot put dummy path in path for cookie or else the cookie won't save
        //store in a session cookie in case tab is refreshed or new tab is opened
        document.cookie =
            "authentication=" +
            encodeURIComponent(authString) +
            ";samesite=strict;path=/"; //;path=/dummyauthpath
        //console.log(`Saving authentication cookie: ${this.status.loggedIn} ${this.status.isGuest} ${this.status.rememberMe}`);

        if (
            this.status.loggedIn &&
            this.status.rememberMe &&
            this.tokens.refreshToken &&
            this.tokens.refreshTokenExpires
        ) {
            //console.log(`Saving authentication localStorage: ${this.status.loggedIn} ${this.status.isGuest} ${this.status.rememberMe}`);
            localStorage.setItem(
                "Authentication",
                JSON.stringify({
                    version: "1",
                    refreshToken: this.tokens.refreshToken,
                    refreshTokenExpires: this.tokens.refreshTokenExpires,
                })
            );
        } else {
            //console.log('Clear authentication localStorage');
            localStorage.removeItem("Authentication");
        }
    };
}
